<script module lang="ts">
    import type { HTMLInputAttributes } from 'svelte/elements';
    export type Props = {
        label?: string;
        error?: null | string;
        value?: CalendarDate;
        class?: string;
        args?: HTMLInputAttributes;
        showValidation?: boolean;
    };
</script>

<script lang="ts">
    import CalendarIcon from '@lucide/svelte/icons/calendar';
    import {
        DateFormatter,
        parseDate,
        getLocalTimeZone,
        today,
        type CalendarDate,
    } from '@internationalized/date';
    import { cn } from '@/lib/utils.js';
    import { buttonVariants } from '@/components/ui/button/index.js';
    import { Calendar } from '@/components/ui/calendar/index.js';
    import * as Popover from '@/components/ui/popover/index.ts';
    import * as Select from '@/components/ui/select/index.ts';
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label';

    let { label, value = $bindable(), error, showValidation = false, ...args }: Props = $props();

    function localDateParts(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    // Reactively convert string or Date to CalendarDate
    let calendarValue = $derived.by(() => {
        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) return undefined;
            return parseDate(localDateParts(value));
        }
        if (typeof value === 'string') {
            // Datetime values (with time component) need local-timezone interpretation
            // so a UTC value like "2026-05-06T23:00:00Z" (= midnight 7 May UK) renders as 7 May.
            if (value.includes('T') || /\d\s\d/.test(value)) {
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return undefined;
                return parseDate(localDateParts(date));
            }
            // Pure YYYY-MM-DD string
            try {
                return parseDate(value);
            } catch {
                return undefined;
            }
        }
        return value;
    });

    const df = new DateFormatter('en-UK', { dateStyle: 'long' });

    const valueString = $derived(calendarValue ? df.format(calendarValue.toDate(getLocalTimeZone())) : '');

    const items = [{ value: 0, label: 'Today' }];

    // Validation classes — same pattern as InternalDateTime
    const validationClasses = $derived(() => {
        if (error) return 'input-error';
        if (showValidation && calendarValue) return 'input-success';
        if (showValidation && !calendarValue) return 'input-error';
        return '';
    });
</script>

<div class="grid gap-2">
    <Label for={label}>
        {label}
        {#if args.required}<span class="text-destructive">*</span>{/if}</Label
    >

    <Popover.Root>
        <Popover.Trigger
            aria-invalid={error || (showValidation && !calendarValue) ? true : undefined}
            class={cn(
                buttonVariants({
                    variant: 'outline',
                    class: 'w-full justify-start text-left font-normal',
                }),
                "selection:bg-primary dark:bg-input/30 selection:text-primary-foreground border-input ring-offset-background placeholder:text-muted-foreground shadow-xs flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-sm font-medium outline-none transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
                !calendarValue && 'text-muted-foreground',
                validationClasses(),
            )}
        >
            <CalendarIcon class="mr-2 size-4" />
            {calendarValue ? df.format(calendarValue.toDate(getLocalTimeZone())) : 'Pick a date'}
        </Popover.Trigger>
        <Popover.Content class="flex w-auto flex-col space-y-2 p-2">
            <Select.Root
                type="single"
                bind:value={
                    () => valueString,
                    (v) => {
                        if (!v) return;
                        const newDate = today(getLocalTimeZone()).add({ days: Number.parseInt(v) });
                        // Output as YYYY-MM-DD string for Laravel
                        value = newDate.toString();
                    }
                }
            >
                <Select.Trigger>
                    {valueString}
                </Select.Trigger>
                <Select.Content>
                    {#each items as item (item.value)}
                        <Select.Item value={`${item.value}`}>{item.label}</Select.Item>
                    {/each}
                </Select.Content>
            </Select.Root>
            <div class="rounded-md border">
                <Calendar
                    type="single"
                    value={calendarValue}
                    onValueChange={(v) => {
                        // Output as YYYY-MM-DD string for Laravel
                        value = v ? v.toString() : null;
                    }}
                />
            </div>
        </Popover.Content>
    </Popover.Root>
    <InputError class="mt-2" message={error} />
</div>
